import React, { useEffect, useRef } from 'react';
import './ProgressSection.css';
import { apiFetch } from '../utils/api';

interface ProcessingResult {
  processor_name: string;
  display_name: string;
  category?: string;
  data: Record<string, any>;
  summary?: string;
}

interface ProgressSectionProps {
  processId: string | null;
  progress: number;
  contentType: string;
  onComplete: (results: ProcessingResult[]) => void;
  onError: (errorMessage: string) => void;
  onProgressUpdate: (progress: number, contentType?: string) => void;
}

interface ProcessStatusResponse {
  status: 'pending' | 'extracting' | 'routing' | 'processing' | 'done' | 'failed';
  progress: number;
  results?: ProcessingResult[];
  error?: string;
}

export const isTransientPollingFailure = (error: unknown): boolean =>
  error instanceof TypeError ||
  (error instanceof DOMException && error.name === "AbortError");

const ProgressSection: React.FC<ProgressSectionProps> = ({
  processId,
  progress,
  contentType,
  onComplete,
  onError,
  onProgressUpdate
}) => {
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isCheckingRef = useRef(false);

  useEffect(() => {
    // Start polling for status updates
    startPolling();

    // Clean up on unmount
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [processId]);

  const startPolling = (): void => {
    // Clear any existing polling interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    // Set initial progress
    onProgressUpdate(0);

    // Poll for status updates every 3 seconds
    pollingIntervalRef.current = setInterval(checkProcessStatus, 3000);

    // Check immediately
    checkProcessStatus();
  };

  const checkProcessStatus = async (): Promise<void> => {
    // Android may resume an old request after another timer tick is queued.
    // Never allow the two responses to race the terminal UI state.
    if (isCheckingRef.current) return;

    if (!processId) {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      return;
    }

    isCheckingRef.current = true;

    try {
      const response = await apiFetch(`/api/links/${processId}/status`);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to get process status');
      }

      const data: ProcessStatusResponse = await response.json();

      // Update progress
      onProgressUpdate(data.progress || 0);

      // Check if the process is complete or failed
      if (data.status === 'done') {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
        }
        // Add a small delay before showing results to ensure all data is processed
        setTimeout(() => {
          onComplete(data.results || []);
        }, 500);
      } else if (data.status === 'failed') {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
        }
        onError(data.error || 'Processing failed');
      }
    } catch (error) {
      // Backgrounding can abort a native WebView fetch. Keep polling so the
      // next request can observe the process that continued on the server.
      if (isTransientPollingFailure(error)) return;

      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      onError(error instanceof Error ? error.message : 'An unknown error occurred');
    } finally {
      isCheckingRef.current = false;
    }
  };

  return (
    <section className="app-section progress-section">
      <div className="app-card">
        <h2 className="app-title">
          <i className="fas fa-sync fa-spin"></i>
          <span>Processing {contentType}...</span>
        </h2>
        <div className="progress-container">
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <div className="progress-text">{progress}%</div>
        </div>
      </div>
    </section>
  );
};

export default ProgressSection;
